<template>
  <daily-prices
    :propBaseProducts.sync="baseProducts"
    :propProductsToShow="productsToShow"
    :propFxSettlement.sync="fxSettlement"
    :propErrorMessage="error"
    :settlementRulesValid="areSettlementRulesValid"
    :hasExistingSettlementValues.sync="hasExistingSettlementValues"
    @on-price-change="onPriceChange"
    @save-settlements="savePricesMethod"
  />
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DailyPrices from "./DailyPricesTemplate.vue";
import { saveSettlementPrices } from "@/api/pricing";
import {
  getBaseSettlementId,
  getStraightProductId,
  getBioProductId,
  getProductDensityReferenceId,
  getProductDutyReferenceId,
} from "@/util/products";
import { calculateFlatPrice, calculateBlendedFlatPrice, calculateLivePrice } from "@/util/calculations";

export default {
  components: {
    DailyPrices,
  },
  data: function () {
    return {
      fxSettlement: "",
      baseProducts: [],
      additionalProducts: [],
      hasExistingSettlementValues: false
    };
  },
  created: async function () {
    await this.getMetadataConfig();
    this.sortProducts(); // Sort available products to base and additional products
    // Assign the stored FX settlement
    if (this.getFxSettlement) this.fxSettlement = this.getFxSettlement.toFixed(4);

    /* Populate prices according to the current market window */
    this.baseProducts.forEach((field) => {
      if (this.getPrices.length > 0) {
        const fuel = this.getPrices.find((data) => data.fuel_id == field.fuel_id);
        if (fuel) field.price = Number(fuel.price || fuel.value).toFixed(2);
      }
    });
    this.checkForExistingSettlementValues();
  },
  computed: {
    ...mapGetters("livePrices", ["marketData"]),
    ...mapGetters("fuelsType", ["getFuelsType"]),
    ...mapGetters("livePrices", ["getPrices", "getFxSettlement"]),
    ...mapGetters("metadata", ["productConfig"]),
    ice: function () {
      const { ice } = this.marketData;
      return {
        live: ice && ice.live,
        settle: ice && ice.settle,
      };
    },
    fxLive: function () {
      const { fx } = this.marketData;
      return fx && fx.live;
    },
    products: function () {
      let data = [];

      if (this.baseProducts && this.additionalProducts) {
        data = [...this.baseProducts, ...this.additionalProducts];
      }

      return data;
    },
    productsToShow: function () {
      const { DieselId, BioDieselId, BlendedDieselBioId, GasoilId, BlendedGasoilId, KeroseneId } = this.productConfig;
      return [
        this.products.find((fuel) => fuel.fuel_id === DieselId),
        this.products.find((fuel) => fuel.fuel_id === BioDieselId),
        this.products.find((fuel) => fuel.fuel_id === BlendedDieselBioId),
        this.products.find((fuel) => fuel.fuel_id === GasoilId),
        this.products.find((fuel) => fuel.fuel_id === BlendedGasoilId),
        this.products.find((fuel) => fuel.fuel_id === KeroseneId),
      ].filter((fuel) => fuel != null );
    },
    triggerPriceCalculation: function () {
      return this.ice && this.fxLive; // ICE values and FX live have just been updated
    },
    error: function () {
      const diesel = this.baseProducts.find((fuel) => fuel.fuel_id === this.productConfig.DieselId);
      const biodiesel = this.baseProducts.find((fuel) => fuel.fuel_id === this.productConfig.BioDieselId);
      const kerosene = this.baseProducts.find((fuel) => fuel.fuel_id === this.productConfig.KeroseneId);

      if (!this.fxSettlement || !diesel.price || !biodiesel.price || !kerosene.price) {
        return "Please enter all four settlement prices";
      } else if (Number(this.fxSettlement) < 0.01 || Number(this.fxSettlement) > 5) {
        return "FX settlement price is invalid";
      } else if (Number(diesel.price) < 50 || Number(diesel.price) > 1000) {
        return "Diesel settlement price is invalid";
      } else if (Number(biodiesel.price) < 50 || Number(biodiesel.price) > 3000) {
        return "Biodiesel settlement price is invalid";
      } else if (Number(kerosene.price) < 50 || Number(kerosene.price) > 1000) {
        return "Kerosene settlement price is invalid";
      } else {
        return "";
      }
    },
    areSettlementRulesValid() {
      return this.getPrices.length > 0;
    },
  },
  watch: {
    triggerPriceCalculation: {
      handler() {
        this.calculatePrices();
      },
      deep: true,
    },
    baseProducts: {
      handler() {
        this.calculatePrices();
      },
      deep: true,
    },
    fxSettlement: {
      handler() {
        this.calculatePrices();
      },
    },
  },
  methods: {
    ...mapActions("metadata", ["getMetadataConfig"]),
    ...mapActions("livePrices", ["savePrices", "closeModal"]),
    ...mapActions("sellerOffers", ["getAllOffersForUser"]),
    checkForExistingSettlementValues() {
      const isInvalidBaseSettlements = this.baseProducts.some(({ price }) => price === "");
      this.hasExistingSettlementValues = this.fxSettlement !== '' && !isInvalidBaseSettlements
    },
    onPriceChange: function (productId, value) {
      const product = this.baseProducts.find(({ fuel_id }) => fuel_id === productId);
      if (product != null) product.price = value;
    },
    sortProducts: function () {
      /* Let's map the products to only obtain relevant information */
      const {
        DieselId,
        BioDieselId,
        BlendedDieselBioId,
        GasoilId,
        BioDieselForGasoilId,
        BlendedGasoilId,
        KeroseneId,
      } = this.productConfig;
      const fuelsList = this.getFuelsType.map((f) => {
        return {
          name: f.name,
          fuel_id: f._id,
          duty: f.duty,
          lpt: f.lpt,
          price: "",
        };
      });
      /* Populate base products */
      this.baseProducts.push(fuelsList.find((f) => f.fuel_id === DieselId));
      this.baseProducts.push(fuelsList.find((f) => f.fuel_id === KeroseneId));
      this.baseProducts.push(fuelsList.find((f) => f.fuel_id === BioDieselId));

      /* Populate remaining additional products */
      this.additionalProducts.push(fuelsList.find((f) => f.fuel_id === GasoilId));
      this.additionalProducts.push(fuelsList.find((f) => f.fuel_id === BioDieselForGasoilId));
      this.additionalProducts.push(fuelsList.find((f) => f.fuel_id === BlendedGasoilId));
      this.additionalProducts.push(fuelsList.find((f) => f.fuel_id === BlendedDieselBioId));
    },
    calculatePrices: function () {
      const fxSettlement = Number(this.fxSettlement);
      const productList = this.products;
      const blendedProductIds = this.$store.getters["metadata/blendedProductConfig"];

      for (const product of productList) {
        const isBlendedProduct = blendedProductIds && blendedProductIds.includes(product.fuel_id);
        let { settlePrice, usdDiff, marketChange } = 0;

        // We calculate blended/non-blended products' settle/flat prices differently
        if (isBlendedProduct) {
          const BIO_BLEND_PERCENTAGE = 0.101;

          // Calculate this product's settle/flat price using its base product's and bio product's settle prices
          const straightProduct = productList.find(
            (p) => p.fuel_id === getStraightProductId(this.productConfig, product.fuel_id)
          );
          const bioProduct = productList.find(
            (p) => p.fuel_id === getBioProductId(this.productConfig, product.fuel_id)
          );
          settlePrice = calculateBlendedFlatPrice(
            BIO_BLEND_PERCENTAGE,
            bioProduct.totalFixed,
            straightProduct.totalFixed
          );

          // Calculate this product's market change using its base product's and bio product's market changes
          product.totalFixed = Number.isFinite(settlePrice) ? Number(settlePrice) : 0;
          product.marketChange = calculateBlendedFlatPrice(
            BIO_BLEND_PERCENTAGE,
            bioProduct.marketChange,
            straightProduct.marketChange
          );
        } else {
          // Get the base product's settlement
          const baseSettlementProduct = productList.find(
            (p) => p.fuel_id === getBaseSettlementId(this.productConfig, product.fuel_id)
          );
          const baseProductSettlement = Number(baseSettlementProduct.price);

          // Get the product's density
          const productDensityReference = productList.find(
            (p) => p.fuel_id === getProductDensityReferenceId(this.productConfig, product.fuel_id)
          );
          const density = productDensityReference.lpt;

          // Get the product's duty
          const productDutyReference = productList.find(
            (p) => p.fuel_id === getProductDutyReferenceId(this.productConfig, product.fuel_id)
          );
          const duty = productDutyReference.duty;

          // Calculate settle/flat price for this product
          settlePrice = calculateFlatPrice(baseProductSettlement, fxSettlement, density, duty);
          product.totalFixed = Number.isFinite(settlePrice) ? Number(settlePrice) : 0;

          // Calculate differential in USD
          usdDiff = baseProductSettlement - this.ice.settle;
          product.usdDiff = Number.isFinite(usdDiff) ? Number(usdDiff) : 0;

          // Calculate market change
          marketChange =
            calculateFlatPrice(this.ice.live + product.usdDiff, this.fxLive, density, duty) - product.totalFixed;
          product.marketChange = Number.isFinite(marketChange) ? Number(marketChange) : 0;
        }

        product.totalLive = calculateLivePrice(product.totalFixed, product.marketChange);
      }
    },
    savePricesMethod: async function (onSaveCallback) {
      const productsList = this.baseProducts;
      const fx = {
        settlement: Number(this.fxSettlement),
        fx_rate_id: "#X.SGBPUSD",
      };

      // Now, let's save these settlement prices
      const response = await saveSettlementPrices(productsList, fx);

      if (response.data.success) {
        /* Prepare object structure to update the store */
        const fuelPrices = [
          ...this.baseProducts.map((field) => {
            return {
              density: Number(field.lpt),
              duty: Number(field.duty),
              fuel_id: field.fuel_id,
              price: Number(field.price),
              totalFixed: Number(field.totalFixed),
              marketChange: Number(field.marketChange),
              totalLive: Number(field.totalLive),
            };
          }),
          ...this.additionalProducts.map((field) => {
            return {
              density: Number(field.lpt),
              duty: Number(field.duty),
              fuel_id: field.fuel_id,
              price: Number(field.price),
              totalFixed: Number(field.totalFixed),
              marketChange: Number(field.marketChange),
              totalLive: Number(field.totalLive),
            };
          }),
        ];

        const storeUpdatePayload = {
          fuelPrices,
          fxSettlement: fx.settlement,
        };
        this.savePrices(storeUpdatePayload); // Update store
        this.getAllOffersForUser(); // Re-fetch offers with updated prices
        onSaveCallback(true);
      } else {
        onSaveCallback(false);
      }
    },
  },
};
</script>

<style></style>
